import { Component, effect, HostListener, inject, OnDestroy, signal, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { SidenavService } from '../../shared/services/sidenav.service';
import { VideoCallStateService } from '../../shared/components/video-call-drawer-chime/video-call-state.service';
import { ChimeService } from '../../shared/components/video-call-drawer-chime/chime.service';

@Component({
  selector: 'app-main-layout',
  standalone: false,
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
})
export class MainLayoutComponent implements OnDestroy {
  public sidenavService = inject(SidenavService);
  public authService = inject(AuthService);

  @ViewChild('sidenav', { read: ElementRef }) sidenavElement!: ElementRef;
  @ViewChild('overlay', { read: ElementRef }) overlayElement!: ElementRef;

  private touchStartX = 0;
  private touchStartY = 0;
  private readonly swipeThreshold = 50; // minimum distance in pixels
  private readonly edgeThreshold = 120;  // start swipe-right within 120px of the left edge to avoid browser back conflict

  isDragging = signal(false);
  private touchStartOpened = false;
  private currentTranslateX = 0;

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent) {
    if (this.sidenavService.mode() !== 'over') return;
    if (event.touches.length > 0) {
      const target = event.target as HTMLElement;
      if (
        target.closest('input') ||
        target.closest('textarea') ||
        target.hasAttribute('contenteditable') ||
        target.closest('.ql-editor') ||
        target.closest('.latex-textarea') ||
        target.closest('button') ||
        target.closest('a') ||
        target.closest('app-whiteboard') ||
        target.closest('app-whiteboard-part') ||
        target.closest('.whiteboard-container') ||
        target.closest('.whiteboard-body') ||
        target.closest('canvas') ||
        target.closest('app-quill-toolbar') ||
        target.closest('.ql-toolbar') ||
        target.closest('.whiteboard-header')
      ) {
        // Skip swipe gestures when interacting with inputs, links, buttons, editors, whiteboards, or toolbars
        this.touchStartX = 0;
        this.touchStartY = 0;
        return;
      }
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.touchStartOpened = this.sidenavService.opened();
    }
  }

  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent) {
    if (this.sidenavService.mode() !== 'over') return;
    if (this.touchStartX === 0) return;
    if (event.touches.length > 0) {
      const currentX = event.touches[0].clientX;
      const currentY = event.touches[0].clientY;
      const diffX = currentX - this.touchStartX;
      const diffY = Math.abs(currentY - this.touchStartY);

      // Ensure swipe is primarily horizontal before intercepting
      if (Math.abs(diffX) > diffY * 1.2) {
        let drawerWidth = this.sidenavElement?.nativeElement?.offsetWidth;
        if (!drawerWidth || drawerWidth === 0) {
          drawerWidth = Math.min(300, window.innerWidth * 0.85);
        }

        if (!this.touchStartOpened) {
          // Opening swipe (swipe right): start near left edge
          if (this.touchStartX <= this.edgeThreshold && diffX > 0) {
            this.isDragging.set(true);
            if (event.cancelable) {
              event.preventDefault();
            }
            // Translate from -drawerWidth to 0
            const translateX = Math.min(0, -drawerWidth + diffX);
            this.currentTranslateX = translateX;
            this.applyStyles(translateX, drawerWidth);
          }
        } else {
          // Closing swipe (swipe left): diffX is negative
          if (diffX < 0) {
            this.isDragging.set(true);
            if (event.cancelable) {
              event.preventDefault();
            }
            // Translate from 0 to -drawerWidth
            const translateX = Math.max(-drawerWidth, diffX);
            this.currentTranslateX = translateX;
            this.applyStyles(translateX, drawerWidth);
          }
        }
      }
    }
  }

  private applyStyles(translateX: number, drawerWidth: number) {
    if (this.sidenavElement?.nativeElement) {
      this.sidenavElement.nativeElement.style.transform = `translate3d(${translateX}px, 0, 0)`;
      this.sidenavElement.nativeElement.style.transition = 'none';
      this.sidenavElement.nativeElement.style.visibility = 'visible';
    }
    // Update overlay opacity dynamically
    const progress = (drawerWidth + translateX) / drawerWidth; // 0 to 1
    if (this.overlayElement?.nativeElement) {
      this.overlayElement.nativeElement.style.opacity = `${progress * 0.32}`;
      this.overlayElement.nativeElement.style.transition = 'none';
    }
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent) {
    if (this.sidenavService.mode() !== 'over') return;
    if (this.touchStartX === 0 && this.touchStartY === 0) return;

    if (this.isDragging()) {
      this.isDragging.set(false);

      // Clean up inline style properties to allow CSS/Material transitions to take over
      if (this.sidenavElement?.nativeElement) {
        this.sidenavElement.nativeElement.style.transform = '';
        this.sidenavElement.nativeElement.style.transition = '';
        this.sidenavElement.nativeElement.style.visibility = '';
      }
      if (this.overlayElement?.nativeElement) {
        this.overlayElement.nativeElement.style.opacity = '';
        this.overlayElement.nativeElement.style.transition = '';
      }

      let drawerWidth = this.sidenavElement?.nativeElement?.offsetWidth;
      if (!drawerWidth || drawerWidth === 0) {
        drawerWidth = Math.min(300, window.innerWidth * 0.85);
      }
      const progress = (drawerWidth + this.currentTranslateX) / drawerWidth;

      if (!this.touchStartOpened) {
        // Opening swipe: succeed if we dragged more than 30% of the width
        if (progress > 0.3) {
          this.sidenavService.open();
        } else {
          this.sidenavService.close();
        }
      } else {
        // Closing swipe: succeed if we closed more than 30% of the width
        if (progress < 0.7) {
          this.sidenavService.close();
        } else {
          this.sidenavService.open();
        }
      }
    } else {
      // Fallback for quick swipes that didn't trigger touchmove drag
      if (event.changedTouches.length > 0) {
        const touchEndX = event.changedTouches[0].clientX;
        const touchEndY = event.changedTouches[0].clientY;
        const diffX = touchEndX - this.touchStartX;
        const diffY = touchEndY - this.touchStartY;

        if (Math.abs(diffX) > Math.abs(diffY) * 1.5) {
          if (diffX > this.swipeThreshold) {
            if (this.touchStartX <= this.edgeThreshold && !this.sidenavService.opened()) {
              this.sidenavService.open();
            }
          } else if (diffX < -this.swipeThreshold) {
            if (this.sidenavService.opened()) {
              this.sidenavService.close();
            }
          }
        }
      }
    }

    // Reset coordinates
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.currentTranslateX = 0;
  }
  private router = inject(Router);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  public videoCallStateService = inject(VideoCallStateService);
  private chimeService = inject(ChimeService);

  videoDrawerOpened = this.videoCallStateService.drawerOpened;
  activeCallId = this.videoCallStateService.activeCallId;

  isChecking = signal(false);
  isResending = signal(false);
  isLoggingOut = signal(false);
  resendCooldown = signal(0);
  private cooldownInterval: any;

  constructor() {
    effect(() => {});
  }

  onVideoDrawerOpenedChange(opened: boolean): void {
    if (!opened && this.videoCallStateService.preventDrawerClose()) {
      // Drawer closed unexpectedly during an active call — force leave meeting
      console.warn('Video call drawer closed unexpectedly during active call. Leaving meeting.');
      this.chimeService.leaveMeeting();
      return;
    }
    this.videoCallStateService.drawerOpened.set(opened);
  }

  isSubMenuOpen() {
    const url = this.router.url;
    return url.includes('/notes') || url.includes('/knowledge/displayer');
  }

  async checkVerification() {
    if (this.isChecking()) return;
    this.isChecking.set(true);
    try {
      const verified = await this.authService.checkEmailVerificationStatus();
      if (verified) {
        this.toast.snackbar.open(this.translate.instant('EmailVerification.ToastVerified'), 'Ok', { duration: 3000 });
      } else {
        this.toast.snackbar.open(this.translate.instant('EmailVerification.ToastNotVerified'), 'Ok', { duration: 3000 });
      }
    } finally {
      this.isChecking.set(false);
    }
  }

  async resendEmail() {
    if (this.isResending() || this.resendCooldown() > 0) return;
    this.isResending.set(true);
    try {
      await this.authService.sendVerificationEmail();
      this.toast.snackbar.open(this.translate.instant('EmailVerification.ToastResent'), 'Ok', { duration: 3000 });
      this.startCooldown(60);
    } catch {
      this.toast.snackbar.open(this.translate.instant('EmailVerification.ToastResendFailed'), 'Ok', { duration: 3000 });
    } finally {
      this.isResending.set(false);
    }
  }

  private startCooldown(seconds: number) {
    this.resendCooldown.set(seconds);
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
    this.cooldownInterval = setInterval(() => {
      const remaining = this.resendCooldown() - 1;
      this.resendCooldown.set(remaining);
      if (remaining <= 0) {
        clearInterval(this.cooldownInterval);
      }
    }, 1000);
  }

  logout() {
    this.isLoggingOut.set(true);
    this.authService.logOut();
  }

  ngOnDestroy() {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
  }
}
